import type { CartOrderForm, CartOrderSummary, CheckoutsOrdersRequest, CheckoutsOrdersResponse, ConfirmCheckoutOrderRequest, ConfirmCheckoutOrderResponse, EventPromoCodeItem, EventTicketsInventoryItem, EventTicketsInventoryResponse, SurveyFormRequest, UserCheckoutOptionsInfo, UserCheckoutOptionsResponse } from '@/types/checkout';
import type { DynamicForm } from '@/types/questionnaire';

interface CheckoutStore {
	// Store cart items when user select ticket on ticket selection page
	cart: {
		promoCode: string
		orders: CartOrderForm[]
	}
	// Step 1: Store ticket info
	ticketsInventoryResponse: {
		data: EventTicketsInventoryResponse | null
		isLoading: boolean
		errorMessage: string,
	},
	// Step 2: Store promotion code
	ticketPromotionsResponse: {
		data: EventPromoCodeItem | null
		isLoading: boolean
		promotionErrorMessage: string,
	},
	// Step3: Store response from create checkout orders
	checkoutOrderResponse: {
		data: CheckoutsOrdersResponse | null
		isLoading: boolean
		errorMessage: string,
	},
	// Step 6 Update order with survey form info
	surveyFormData: DynamicForm,
	surveyFormResponse: {
		isLoading: boolean,
		errorMessage: string
	},
	// Step 7: Store response from User's preference e.g. save card, follow organizer
	userCheckoutOptionsResponse: {
		data: UserCheckoutOptionsResponse | null
		isLoading: boolean
		errorMessage: string,
	},
	// Step 8: Store loading and error message when update User's preference
	updateCheckoutOrderResponse: {
		isLoading: boolean
		errorMessage: string,
	},
	// Step 9: Store response after send to API to confirm check out order
	confirmCheckoutOrderResponse: {
		data: ConfirmCheckoutOrderResponse | null
		isLoading: boolean
		errorMessage: string,
	},
	// Step 10: Store order status response from polling process
	checkoutOrderStatusResponse: {
		orderSalesId: number | null
		orderStatus: string | null
		isLoading: boolean
		errorMessage: string,
	},
}

const useCheckoutStore = defineStore('checkout', {

	state: (): CheckoutStore => ({
		cart: {
			promoCode: '',
			orders: [],
		},
		ticketsInventoryResponse: {
			data: null,
			isLoading: false,
			errorMessage: '',
		},
		ticketPromotionsResponse: {
			data: null,
			isLoading: false,
			promotionErrorMessage: '',
		},
		checkoutOrderResponse: {
			data: null,
			isLoading: false,
			errorMessage: '',
		},
		surveyFormData: {},
		surveyFormResponse: {
			isLoading: false,
			errorMessage: '',
		},
		userCheckoutOptionsResponse: {
			data: null,
			isLoading: false,
			errorMessage: '',
		},
		updateCheckoutOrderResponse: {
			isLoading: false,
			errorMessage: '',
		},
		confirmCheckoutOrderResponse: {
			data: null,
			isLoading: false,
			errorMessage: '',
		},
		checkoutOrderStatusResponse: {
			isLoading: false,
			orderSalesId: null,
			orderStatus: null,
			errorMessage: '',
		},
	}),

	getters: {
		ticketsInventoryList: (state): EventTicketsInventoryItem[] => {
			if (!state.ticketsInventoryResponse.data?.data) {
				return [];
			}
			return transformTicketsInventoryList(state.ticketsInventoryResponse.data.data, state.ticketPromotionsResponse.data);
		},

		orderSummary: (state: CheckoutStore): CartOrderSummary => {
			const orderSummary = transformCartToOrderSummary(state.cart.orders);

			if (state.checkoutOrderResponse.data?.totalAmount) {
				// Use totalAmount form BE calculation
				return {
					...orderSummary,
					total: state.checkoutOrderResponse.data?.totalAmount,
				};
			}
			return orderSummary;
		},

		userCheckoutOptions: (state: CheckoutStore): UserCheckoutOptionsInfo | null => {
			if (!state.userCheckoutOptionsResponse.data) {
				return null;
			}
			return transformUserCheckoutOptions(state.userCheckoutOptionsResponse.data);
		},

		stripePublishableKey: (state: CheckoutStore): string | null => {
			if (!state.checkoutOrderResponse.data) {
				return null;
			}
			return state.checkoutOrderResponse.data.paymentSecrets.publicKey;
		},

		stripeAccountId: (state: CheckoutStore): string | null => {
			if (!state.checkoutOrderResponse.data) {
				return null;
			}
			return state.checkoutOrderResponse.data.paymentSecrets.destinationAccount;
		},

		surveyFormId: (state): number | null => {
			if (!state.ticketsInventoryResponse.data?.data) {
				return null;
			}
			return state.ticketsInventoryResponse.data?.formId;
		},

		paymentSecret: (state): string | null => {
			if (!state.confirmCheckoutOrderResponse.data) {
				return null;
			}
			return state.confirmCheckoutOrderResponse.data.paymentSecret;
		},

		paymentUrl: (state): string | null => {
			if (!state.confirmCheckoutOrderResponse.data) {
				return null;
			}
			return state.confirmCheckoutOrderResponse.data.paymentUrl;
		},

		salesId: (state): number | null => {
			if (!state.checkoutOrderStatusResponse.orderSalesId) {
				return null;
			}
			return state.checkoutOrderStatusResponse.orderSalesId;
		},

		orderTicketIds: (state: CheckoutStore) => {
			if (!state.cart.orders) {
				return '';
			}
			const orderSummary = transformCartToOrderSummary(state.cart.orders);
			const ids = orderSummary.cartItems.map((item) => {
				return item.id;
			}).join(',');
			return ids;
		},
	},

	actions: {
		async getTicketsInventory(eventId: number) {
			this.ticketsInventoryResponse.isLoading = true;
			this.ticketsInventoryResponse.errorMessage = '';

			const { data, errorMessage } = await getTicketsInventoryAPI(eventId);

			if (errorMessage.value) {
				this.ticketsInventoryResponse.errorMessage = errorMessage.value;
			}

			if (data.value) {
				this.ticketsInventoryResponse.data = data.value;
			}
			this.ticketsInventoryResponse.isLoading = false;
		},

		async getTicketPromotions(eventId: number, promoCode: string) {
			this.ticketPromotionsResponse.isLoading = true;
			this.ticketPromotionsResponse.promotionErrorMessage = '';
			const { data, errorMessage } = await getTicketsPromotionsAPI(eventId, promoCode);

			if (errorMessage.value) {
				this.ticketPromotionsResponse.promotionErrorMessage = 'This code is invalid.';
			}

			if (data.value?.data?.length) {
				// NOTE: Peasy currently support only 1 promocode
				this.ticketPromotionsResponse.data = data.value.data?.[0];
			}
			this.ticketPromotionsResponse.isLoading = false;
		},

		async createCheckoutOrder(payload: Partial<CheckoutsOrdersRequest>) {
			this.checkoutOrderResponse.errorMessage = '';
			this.checkoutOrderResponse.isLoading = true;

			const { data, errorMessage } = await postCheckoutsOrdersAPI(payload);

			if (errorMessage.value) {
				this.checkoutOrderResponse.errorMessage = errorMessage.value;
			}

			if (data.value) {
				this.checkoutOrderResponse.data = data.value;
			}
			this.checkoutOrderResponse.isLoading = false;
		},

		async getUserCheckoutOptionsById(userId: number, eventId: number) {
			this.userCheckoutOptionsResponse.isLoading = true;
			this.userCheckoutOptionsResponse.errorMessage = '';

			const { data, errorMessage } = await getUserCheckoutOptionsAPI(userId, eventId);

			if (errorMessage.value) {
				this.userCheckoutOptionsResponse.errorMessage = errorMessage.value;
			}

			if (data.value) {
				this.userCheckoutOptionsResponse.data = data.value;
			}
			this.userCheckoutOptionsResponse.isLoading = false;
		},

		async updateCheckoutOrderBuyerInfoByOrderId(orderId: string, payload: Partial<CheckoutsOrdersRequest>) {
			this.updateCheckoutOrderResponse.isLoading = true;
			this.updateCheckoutOrderResponse.errorMessage = '';

			const { errorMessage } = await patchCheckoutOrderBuyerInfoByOrderIdAPI(orderId, payload);

			if (errorMessage.value) {
				this.updateCheckoutOrderResponse.errorMessage = errorMessage.value;
			}

			this.updateCheckoutOrderResponse.isLoading = false;
		},

		async confirmCheckoutOrdersByOrderId(orderId: string, payload: ConfirmCheckoutOrderRequest) {
			this.confirmCheckoutOrderResponse.isLoading = true;
			this.confirmCheckoutOrderResponse.errorMessage = '';

			const { data, errorMessage } = await postConfirmCheckoutsOrdersAPI(orderId, payload);

			if (errorMessage.value) {
				this.confirmCheckoutOrderResponse.errorMessage = errorMessage.value;
			}

			if (data.value) {
				this.confirmCheckoutOrderResponse.data = data.value;
			}

			this.confirmCheckoutOrderResponse.isLoading = false;
		},

		async getCheckoutOrdersStatusById(orderId: string) {
			this.checkoutOrderStatusResponse.isLoading = true;
			this.checkoutOrderStatusResponse.errorMessage = '';
			this.checkoutOrderStatusResponse.orderStatus = '';

			const { data, errorMessage } = await getCheckoutOrdersStatusAPI(orderId);

			if (errorMessage.value) {
				this.checkoutOrderStatusResponse.errorMessage = errorMessage.value;
			}

			if (data.value) {
				this.checkoutOrderStatusResponse.orderStatus = data.value.status;
				this.checkoutOrderStatusResponse.orderSalesId = data.value.salesId;
			}

			this.checkoutOrderStatusResponse.isLoading = false;
		},

		async updateSurveyFormOrderId(orderId: string, payload: SurveyFormRequest) {
			this.surveyFormResponse.errorMessage = '';
			this.surveyFormResponse.isLoading = true;

			const { errorMessage } = await patchSurveyFormByOrderIdAPI(orderId, payload);

			if (errorMessage.value) {
				this.surveyFormResponse.errorMessage = errorMessage.value;
			}
			this.surveyFormResponse.isLoading = false;
		},
	},
});

export default useCheckoutStore;
